'use client'

import { useEffect, useRef, useCallback } from 'react'

interface SmoothScrollOptions {
  duration?: number
  easing?: 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'cubic' | 'expo'
  speed?: number // Time in ms to scroll 1000px (like Smooth Scroll library)
  wheelMultiplier?: number
  touchMultiplier?: number
  smoothWheel?: boolean
  smoothTouch?: boolean
  normalizeScroll?: boolean // Force JS thread scrolling
  ignoreMobileResize?: boolean
}

// Easing functions based on research from smooth-scroll libraries
const easingFunctions = {
  linear: (t: number) => t,
  easeIn: (t: number) => t * t,
  easeOut: (t: number) => 1 - Math.pow(1 - t, 2),
  easeInOut: (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
  cubic: (t: number) => 1 - Math.pow(1 - t, 3),
  expo: (t: number) => t === 0 ? 0 : Math.pow(2, 10 * t - 10)
}

export default function useCustomSmoothScroll(options: SmoothScrollOptions = {}) {
  const {
    duration = 800, // Default duration
    easing = 'cubic', // Default to cubic easing
    speed = 800, // Time to scroll 1000px
    wheelMultiplier = 1.2,
    touchMultiplier = 1.5,
    smoothWheel = true,
    smoothTouch = false,
    normalizeScroll = true,
    ignoreMobileResize = true
  } = options

  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const isScrollingRef = useRef(false)
  const animationFrameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)
  const startScrollTopRef = useRef<number>(0)
  const targetScrollTopRef = useRef<number>(0)
  const lastWheelTimeRef = useRef<number>(0)
  const scrollQueueRef = useRef<Array<{ target: number; priority: number }>>([])

  // Get scroll container
  const getScrollContainer = useCallback(() => {
    if (!scrollContainerRef.current) {
      scrollContainerRef.current = document.querySelector('.smooth-content') as HTMLElement
    }
    return scrollContainerRef.current
  }, [])

  // Calculate duration based on distance and speed (like Smooth Scroll library)
  const calculateDuration = useCallback((distance: number) => {
    return (distance / 1000) * speed
  }, [speed])

  // Animate scroll to target position with smooth transitions
  const animateScroll = useCallback((targetScrollTop: number, customDuration?: number) => {
    const container = getScrollContainer()
    if (!container) return

    const startScrollTop = container.scrollTop
    const distance = Math.abs(targetScrollTop - startScrollTop)

    if (distance < 1) return

    const actualDuration = customDuration || calculateDuration(distance)
    const easingFunction = easingFunctions[easing]

    startTimeRef.current = performance.now()
    startScrollTopRef.current = startScrollTop
    targetScrollTopRef.current = targetScrollTop
    isScrollingRef.current = true

    const animate = (currentTime: number) => {
      if (!isScrollingRef.current) return

      const elapsed = currentTime - startTimeRef.current
      const progress = Math.min(elapsed / actualDuration, 1)
      const easedProgress = easingFunction(progress)

      const currentScrollTop = startScrollTopRef.current + ((targetScrollTop - startScrollTop) * easedProgress)
      container.scrollTop = currentScrollTop

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        isScrollingRef.current = false
        container.scrollTop = targetScrollTop

        // Process next item in queue
        if (scrollQueueRef.current.length > 0) {
          const nextScroll = scrollQueueRef.current.shift()
          if (nextScroll) {
            animateScroll(nextScroll.target)
          }
        }
      }
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    animationFrameRef.current = requestAnimationFrame(animate)
  }, [duration, easing, calculateDuration, getScrollContainer])

  // Handle wheel events with smooth transitions
  const handleWheel = useCallback((event: WheelEvent) => {
    if (!smoothWheel) return

    event.preventDefault()
    event.stopPropagation()

    const container = getScrollContainer()
    if (!container) return

    const currentTime = performance.now()
    const timeSinceLastWheel = currentTime - lastWheelTimeRef.current
    lastWheelTimeRef.current = currentTime

    const delta = event.deltaY * wheelMultiplier
    const currentScrollTop = container.scrollTop

    // If we're already scrolling and it's been less than 100ms, update the target
    if (isScrollingRef.current && timeSinceLastWheel < 100) {
      const currentTarget = targetScrollTopRef.current
      const newTarget = Math.max(0, Math.min(
        currentTarget + delta,
        container.scrollHeight - container.clientHeight
      ))
      targetScrollTopRef.current = newTarget
      return
    }

    const targetScrollTop = Math.max(0, Math.min(
      currentScrollTop + delta,
      container.scrollHeight - container.clientHeight
    ))

    animateScroll(targetScrollTop)
  }, [smoothWheel, wheelMultiplier, animateScroll, getScrollContainer])

  // Handle touch events
  const touchStartRef = useRef<number>(0)
  const touchScrollStartRef = useRef<number>(0)

  const handleTouchStart = useCallback((event: TouchEvent) => {
    if (!smoothTouch) return

    const container = getScrollContainer()
    if (!container) return

    if (event.touches.length > 0) {
      touchStartRef.current = event.touches[0]?.clientY ?? 0
      touchScrollStartRef.current = container.scrollTop
    }
  }, [smoothTouch, getScrollContainer])

  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (!smoothTouch) return

    event.preventDefault()
    event.stopPropagation()

    const container = getScrollContainer()
    if (!container) return

    if (event.touches.length > 0) {
      const currentY = event.touches[0]?.clientY ?? touchStartRef.current
      const deltaY = (touchStartRef.current - currentY) * touchMultiplier
      const targetScrollTop = Math.max(0, Math.min(
        touchScrollStartRef.current + deltaY,
        container.scrollHeight - container.clientHeight
      ))

      animateScroll(targetScrollTop)
    }
  }, [smoothTouch, touchMultiplier, animateScroll, getScrollContainer])

  // Initialize event listeners
  useEffect(() => {
    const container = getScrollContainer()
    if (!container) return

    // Add wheel event listener
    container.addEventListener('wheel', handleWheel, { passive: false })

    // Add touch event listeners
    if (smoothTouch) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true })
      container.addEventListener('touchmove', handleTouchMove, { passive: false })
    }

    return () => {
      container.removeEventListener('wheel', handleWheel)
      if (smoothTouch) {
        container.removeEventListener('touchstart', handleTouchStart)
        container.removeEventListener('touchmove', handleTouchMove)
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [getScrollContainer, handleWheel, handleTouchStart, handleTouchMove, smoothTouch])

  // Public API
  const scrollTo = useCallback((targetScrollTop: number, customDuration?: number) => {
    animateScroll(targetScrollTop, customDuration)
  }, [animateScroll])

  const scrollBy = useCallback((delta: number) => {
    const container = getScrollContainer()
    if (!container) return

    const currentScrollTop = container.scrollTop
    const targetScrollTop = Math.max(0, Math.min(
      currentScrollTop + delta,
      container.scrollHeight - container.clientHeight
    ))
    animateScroll(targetScrollTop)
  }, [animateScroll, getScrollContainer])

  const scrollToElement = useCallback((element: HTMLElement, offset = 0) => {
    const container = getScrollContainer()
    if (!container || !element) return

    const containerRect = container.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const targetScrollTop = container.scrollTop + (elementRect.top - containerRect.top) - offset

    animateScroll(targetScrollTop)
  }, [animateScroll, getScrollContainer])

  const scrollToAnchor = useCallback((anchor: string, offset = 0) => {
    const element = document.querySelector(anchor) as HTMLElement
    if (element) {
      scrollToElement(element, offset)
    }
  }, [scrollToElement])

  return {
    scrollTo,
    scrollBy,
    scrollToElement,
    scrollToAnchor,
    isScrolling: isScrollingRef.current
  }
}
